import React from "react";

interface DeletePhoneModalProps {
  show: boolean;
  /** Formatted phone number for single delete (e.g. "(224) 516-0086"). Null for bulk delete. */
  phoneNumber: string | null;
  /** Count of selected phone numbers for bulk delete. Ignored for single delete. */
  bulkCount?: number;
  /** Whether the delete operation is in progress. */
  isDeleting?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Delete phone number confirmation dialog.
 * Matches the MVC Kendo Dialog exactly:
 *   Single: "Delete phone number" / "Would you like to delete this phone number (224) 516-0086?"
 *   Bulk:   "Delete phone number(s)" / "Are you sure you want to delete the selected phone numbers?"
 * Buttons: Cancel (secondary) + Ok (primary)
 */
const DeletePhoneModal: React.FC<DeletePhoneModalProps> = ({
  show,
  phoneNumber,
  isDeleting = false,
  onConfirm,
  onCancel,
}) => {
  if (!show) return null;

  const isBulk = phoneNumber === null;

  return (
    <div
      className="modal d-block"
      tabIndex={-1}
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog" style={{ maxWidth: 500 }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {isBulk ? "Delete phone number(s)" : "Delete phone number"}
            </h5>
            <button type="button" className="btn-close" onClick={onCancel} disabled={isDeleting} />
          </div>
          <div className="modal-body">
            {isDeleting ? (
              <div className="d-flex align-items-center gap-2">
                <span className="spinner-border spinner-border-sm" />
                <span>Deleting...</span>
              </div>
            ) : isBulk ? (
              <p>Are you sure you want to delete the selected phone numbers?</p>
            ) : (
              <p>
                Would you like to delete this phone number{" "}
                <strong>{phoneNumber}</strong>?
              </p>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onConfirm}
              disabled={isDeleting}
            >
              Ok
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeletePhoneModal;
